import { MAIN_BRANCH, RECOMPUTE_TEST_SUITE_ID, RELEASE_BRANCH, WEB_E2E_SUITE_ID } from '@finvari/deliver';

// The single source of truth for the PR workflow: the labels that park or flag a PR, the scoped-test
// lanes (and the path rules that make each one required), and the diagram's node/edge layout. Adding
// a new scoped test later should be a single TEST_LANES entry — the derivation, the diagram nodes,
// and the panel tab all read from here. The branch names come from the shared package, since the
// backend compares the same two branches to work out what is still unreleased.

// The GitHub repo this workflow tracks. Used to build source-tree links in the help panel and the
// PR link base, so the org/repo lives in exactly one place.
export const GITHUB_REPO_URL = 'https://github.com/finvari/payments';

// A browsable link to a directory on main, e.g. githubTreeUrl('automation/web-e2e').
export function githubTreeUrl(repoRelativePath: string): string {
  return GITHUB_REPO_URL + '/tree/' + MAIN_BRANCH + '/' + repoRelativePath;
}

// Where the Deployer promotes merged changes from main onto the release branch. Not deployed yet, so
// this will 404 until the Deployer ships — it is here now so the Merged help points at the right place.
export const DEPLOYER_URL = 'https://release.finvari.com';

// Labels that park a PR (hold it before "Open") or flag it. Peers of the draft state.
export const PARKED_LABELS = { paused: 'paused', stuck: 'stuck', wip: 'wip' } as const;
export const PRIORITY_LABEL = 'priority';

export type LaneStatus =
  | 'passed'          // green / not blocking (fresh pass)
  | 'running'         // a run is in progress
  | 'failed'          // last run failed, not rerun
  | 'verify'          // last run finished (completed or errored) but no human has verified it yet
  | 'needed'          // required by path rules but never run
  | 'retest-decision' // last run passed but is behind head; a human must rerun or accept
  | 'na';             // not required and not run

// 'needed' is GitHub having registered no check runs at all for the head — the tests have not
// started, as opposed to 'running' where GitHub has picked the commit up and is working on it.
export type UnitStatus = 'passed' | 'needed' | 'running' | 'failed' | 'na';

// Approval is a green state, so like every other lane's pass it is represented by absence — a PR
// leaves the review row rather than landing in an "approved" node. The other three stages are
// real positions a PR can occupy.
export type ReviewStage = 'open' | 'reviewing' | 'changes-requested' | 'approved';

export type ParkedReason = 'upstream-wait' | 'draft' | 'paused' | 'stuck' | 'wip';

export interface TestLaneDef {
  id: string;          // stable lane id, also the node-id stem (e.g. 'recompute' -> 'needs-recompute')
  label: string;       // human label used in the panel tab and the node's stage explanation
  shortLabel: string;  // the diagram label for the lane's "testing" box (parallel to "Unit testing")
  suiteId: string;     // the test suite this lane tracks
  // One sentence describing what this test is, reused across the lane's help nodes so it lives once.
  summary: string;
  // Help copy for the "needs" queue node, preferred over `summary` there. Kept separate because the
  // queue's job is to explain why the PR is stuck and what to choose, not to repeat "some differences
  // are expected" (which belongs on the results, not the queue). Each string is a paragraph and may
  // contain **bold** spans. Falls back to `summary` when absent.
  neededDetails?: string[];
  // Help copy for the "testing" (running) node, preferred over `summary` there — the lane's glossary,
  // written as prose with **bold** terms. Falls back to `summary` when absent.
  testingDetails?: string[];
  // Repo-relative directory the lane's test code lives in, linked from the help panel.
  sourcePath: string;
  // Additional "Learn more" links appended to the lane's run nodes (e.g. a local-runner README).
  extraHelpLinks?: HelpLink[];
  // A PR "needs" this test when any changed file path matches one of these patterns.
  requiredWhen: RegExp[];
  // When set, a finished run is never automatically green: a human must inspect its results and mark
  // it verified before the lane passes. Used for recompute, whose runs always carry expected diffs
  // and error logs today, and where "failure" is a human judgement over the results rather than a
  // status the system can pronounce. The lane's failure node becomes a "Verify" step instead.
  verify?: boolean;
}

// The scoped tests, in the order their lanes appear top-to-bottom under the review lane.
export const TEST_LANES: TestLaneDef[] = [
  {
    id: 'recompute',
    label: 'Recompute',
    shortLabel: 'Recompute Testing',
    suiteId: RECOMPUTE_TEST_SUITE_ID,
    summary:
      'Recompute is our compiler-style regression test. It deploys the PR to a test environment, ' +
      'recomputes and re-exports a set of real customer programs restored from snapshots, then diffs ' +
      'the resulting database contents against a baseline execution. Some differences are expected ' +
      '(masked timestamps, intentional changes); unexpected ones expose behaviour changes that unit ' +
      "tests can't reach through complicated customer configs and hard-to-test platform capabilities.",
    // The "Needs Recompute" queue: answer "why is my PR stuck here", then the data constraint, then
    // how to choose programs. Deliberately does NOT say "some differences are expected" — that framing
    // belongs on the results, not the queue.
    neededDetails: [
      "This PR changes compute, expense, export, or connector logic (the paths listed below), and nothing has run against its head commit yet. This is a queue, not a failure — start a run from the PR's Recompute tab.",
      'A **Recompute Test** is a **snapshot test**, not an assertion suite. It recomputes and re-exports real customer programs on a deployed environment and diffs the resulting database contents against a **baseline** — an earlier run whose output was recorded as the reference.',
      "The comparison only means something if the environment holds the same customer data the baseline ran against. Each program's data is identified by a **test data signature** — the date of the backup snapshot it was restored from. When signatures differ, that program's diff is dominated by data drift and can't be separated from your code change afterward. The run dialog names any mismatch and requires an explicit acknowledgement before it will start.",
      'Each **subject** is a full compute-and-diff over one customer dataset, so more programs means a longer run and more to review, while a program left out is a code path untested. Choose the programs whose configuration exercises what you changed.',
    ],
    // The "Recompute Testing" stage: the lane's glossary, in prose with the key terms bolded.
    testingDetails: [
      'A **Recompute Test** deploys the PR to a test environment, recomputes and re-exports a set of real customer programs, and diffs the result against a baseline. Each program in the run is a **subject**.',
      "A subject's data is restored from a **snapshot**, and that snapshot's date is its **test data signature**. A **baseline** is an earlier run whose output is the reference this run diffs against; a run started without a baseline skips the **diff** step and becomes a baseline itself. A **golden baseline** is the suite's pinned reference, so successive runs of a PR stay comparable.",
      'The **diff** is grouped into **review items**, each a distinct shape of change seen across the programs. **Pattern verification** is working through those items; a **pattern acknowledgement** is your accept or reject of one item. An **attestation** item flags a program that showed no evidence of actually recomputing. Whole-run **verification** stays blocked until every item is accepted.',
    ],
    sourcePath: 'functions/src/deliver/recomputeTest',
    extraHelpLinks: [{ label: 'Local runner (functions/tests)', href: githubTreeUrl('functions/tests') }],
    // Recompute is required when compute/expense/export/connector business logic changes.
    requiredWhen: [
      /^functions\/src\/(connector|compute|expenses|export)\//,
      /^model\/src\/(connector|expenses|exports)\//,
    ],
    // A recompute run always produces some diffs (masked timestamps, and today some we still need to
    // fix) and some error logs, and "failure" is a human call over those results — so a finished run
    // waits in Verify for a person to inspect it and accept it, rather than auto-passing to Ready.
    verify: true,
  },
  {
    id: 'e2e',
    label: 'e2e',
    shortLabel: 'e2e Testing',
    suiteId: WEB_E2E_SUITE_ID,
    summary:
      'Full-stack end-to-end Playwright tests of key use-cases, run against a deployed environment ' +
      'using the acme_vb demo program — the program we use for sales demos, which holds no customer data.',
    sourcePath: 'automation/web-e2e',
    // e2e is required when the web or saml modules change.
    requiredWhen: [/^web\//, /^saml\//],
  },
];

// Render a "required when" path rule as the plain repo path it matches, for the help panel: strips the
// anchoring `^` and unescapes `\/` so /^functions\/src\/(a|b)\// reads as functions/src/(a|b)/.
export function pathRuleText(re: RegExp): string {
  return re.source.replace(/^\^/, '').replace(/\\\//g, '/');
}

// Node ids for a scoped test lane, derived from its lane id so they stay in lock-step with TEST_LANES.
// A lane has either a `failed` node (pass/fail lanes) or a `verify` node (verify lanes) in the same
// diagram slot; both ids are derived so the two stay predictable.
export function laneNodeIds(laneId: string): {
  needed: string;
  running: string;
  failed: string;
  verify: string;
  retest: string;
} {
  return {
    needed: 'needs-' + laneId,
    running: laneId + '-testing',
    failed: laneId + '-failures',
    verify: laneId + '-verify',
    retest: laneId + '-retest',
  };
}

// Well-known non-lane node ids.
export const NODE = {
  upstreamWait: 'upstream-wait',
  draft: 'draft',
  paused: 'paused',
  stuck: 'stuck',
  wip: 'wip',
  open: 'open',
  reviewing: 'reviewing',
  changesRequested: 'changes-requested',
  mergeConflicts: 'merge-conflicts',
  ready: 'ready',
  merged: 'merged',
  // Named like the scoped lanes' queues ('needs-' + lane id) since it plays the same role for unit.
  unitNeeded: 'needs-unit',
  unitTesting: 'unit-testing',
  unitFailures: 'unit-failures',
} as const;

// The container node that groups the parked states into a single labelled area with one arrow
// into Open (instead of five separate parked nodes each drawing their own edge).
export const PARKED_GROUP = 'parked-group';

export type WorkflowNodeKind = 'parked' | 'review' | 'terminal' | 'unit' | 'test' | 'group';

// How a node is drawn. Boxes are full-size lifecycle stages that sit on a row's flow line; chips are
// smaller side states that hang above/below the line; count boxes are label-less badges (the "needs"
// queues); the gate is the tall Ready bar every lane funnels into; group is the Parked container.
export type NodeVariant = 'box' | 'chip' | 'count' | 'gate' | 'group';

// Which horizontal flow line a node belongs to. 'unit' and 'review' are fixed rows; a scoped test
// lane uses its own laneId as the row id, so adding a TEST_LANES entry adds a row automatically.
export type RowId = 'unit' | 'review' | string;

// Where a node sits relative to its row's flow line: 0 = on the line, n > 0 = the nth chip tier
// hanging below it. Everything hangs below (nothing above) so each lane reads top-down — the line
// running into Ready first, then its side states beneath — which keeps the recompute/e2e/unit
// channels visually separated instead of interleaving a lane's Retest? with the next lane's Failures.
export type Slot = number;

export type Side = 'left' | 'right' | 'top' | 'bottom';

// A "Learn more" link shown under a stage's explanation in the help panel.
export interface HelpLink {
  label: string;
  href: string;
}

export interface WorkflowNodeDef {
  id: string;
  label: string;         // full human label (the panel's stage heading, and the table's stage chip)
  diagramLabel: string;  // what the diagram renders ('' => a count-only badge with no text)
  description: string;   // the stage explanation shown in the detail panel when the stage is selected
  // Extra help paragraphs shown under `description` (background, and what a dev should do next).
  details?: string[];
  // On the "needs" queue nodes: the path rules that make this test required, rendered as a monospace
  // block so a dev can see exactly which changed files trigger the lane.
  pathRules?: RegExp[];
  // "Learn more" links (e.g. the lane's source directory, the Deployer).
  links?: HelpLink[];
  // Ids of other workflow stages to link to from this stage's help (e.g. the "needs" queue linking to
  // the lane's glossary stage). Rendered as in-app links that select the target stage, unlike `links`
  // which are external anchors.
  stageLinks?: string[];
  kind: WorkflowNodeKind;
  variant: NodeVariant;
  col: number;           // grid column; pixel x is computed by workflowLayout
  row: RowId;            // grid row; pixel y is computed by workflowLayout
  slot: Slot;
  // A failure side-state. Its badge uses one alarming tone regardless of lane, so a teal test lane's
  // failures read as urgent as the orange unit lane's rather than staying a calm teal.
  failure?: boolean;
  laneId?: string;
  parentId?: string;     // set on parked chips so they render inside the Parked group
}

export interface WorkflowEdgeDef {
  source: string;
  target: string;
  // Which side of each node the edge attaches to (defaults: source right, target left). Picking
  // sides keeps the curves clean — e.g. Reviewing's bottom to Changes requested's top.
  sourceSide?: Side;
  targetSide?: Side;
}

const PARKED_GROUP_NODE: WorkflowNodeDef = {
  id: PARKED_GROUP, label: 'Parked', diagramLabel: 'Parked', kind: 'group', variant: 'group',
  col: 0, row: 'review', slot: 0,
  description: 'Held before Open: waiting on an upstream PR, a GitHub draft, or carrying a paused / stuck / wip label.',
  // The diagram charts progress toward Ready rather than inventorying every fact about a PR, so a
  // parked PR is charted here and nowhere else: nobody acts on a draft's or a paused PR's merge
  // conflicts or unit checks until it is unparked, and listing them under those stages would only pad
  // the counts with PRs that aren't moving. The PR's own Overview tab still lists them as blockers.
  details: ['A parked PR sits only here. Its merge conflicts and unit checks are not counted under those stages until it is unparked, though they still appear in its Overview.'],
};

// The parked states are full-size boxes like Reviewing rather than smaller chips: the Parked column
// has spare vertical room, and a box-height click target is much easier to hit than a 16px chip.
const PARKED_NODES: WorkflowNodeDef[] = [
  { id: NODE.upstreamWait, label: 'Upstream wait', diagramLabel: 'Upstream wait', kind: 'parked', variant: 'box', col: 0, row: 'review', slot: 0, parentId: PARKED_GROUP,
    description: 'The PR targets a branch other than main, so it is stacked on another PR and waits for that one to merge first.',
    details: ['GitHub retargets it to main automatically when the base branch merges. You can still run tests against it while it waits.'] },
  { id: NODE.draft, label: 'Draft', diagramLabel: 'Draft', kind: 'parked', variant: 'box', col: 0, row: 'review', slot: 0, parentId: PARKED_GROUP,
    description: 'The PR is a GitHub draft and is not yet eligible for testing here.',
    details: ['A draft signals the author is still finalising it — or wants an early look from someone — before opening it for formal review. Mark it "Ready for review" on GitHub to move it to Open.'] },
  { id: NODE.paused, label: 'Paused', diagramLabel: 'Paused', kind: 'parked', variant: 'box', col: 0, row: 'review', slot: 0, parentId: PARKED_GROUP,
    description: 'The PR carries the "paused" label — it is in a holding pattern for something outside this system.',
    details: ['Use paused for experimental work we may or may not take forward, or a PR blocked on a dependency that isn\'t itself a PR. Remove the label to resume.'] },
  { id: NODE.stuck, label: 'Stuck', diagramLabel: 'Stuck', kind: 'parked', variant: 'box', col: 0, row: 'review', slot: 0, parentId: PARKED_GROUP,
    description: 'The PR carries the "stuck" label — nobody currently knows how to move it forward and it needs help.',
    details: ['If you can unblock it, comment on the PR or pick it up, then remove the label.'] },
  { id: NODE.wip, label: 'WIP', diagramLabel: 'WIP', kind: 'parked', variant: 'box', col: 0, row: 'review', slot: 0, parentId: PARKED_GROUP,
    description: 'The PR carries the "wip" label — still being worked, but slowly; progress has stalled.',
    details: ['Unlike a draft (which is being finalised for imminent review), a wip PR has no near-term path to review. Remove the label when active work resumes.'] },
];

const REVIEW_NODES: WorkflowNodeDef[] = [
  { id: NODE.open, label: 'Open', diagramLabel: 'Open', kind: 'review', variant: 'box', col: 1, row: 'review', slot: 0,
    description: 'The PR is no longer draft, and no one other than the author has engaged with it yet — no review and no comment.',
    details: ['GitHub starts the unit tests automatically; until it has registered them the PR sits in the unit queue below Open. If the changed files match a scoped lane\'s path rules, that lane\'s queue picks it up too.'] },
  { id: NODE.reviewing, label: 'Reviewing', diagramLabel: 'Reviewing', kind: 'review', variant: 'box', col: 2, row: 'review', slot: 0,
    description: 'Someone other than the author has engaged with the PR — left a review comment or a plain comment — but has not yet approved it.',
    // A plain top-level comment counts here on purpose: it is a team-dynamics signal, not a mechanical
    // gate. It tells us a second person is aware of this PR enough to have written something on it,
    // which is exactly the transition out of "only the author has touched this" that we want to see.
    details: ['A reviewer\'s approval moves the PR straight to Ready (once its tests are green); a request for changes moves it to Changes requested.'] },
  { id: NODE.mergeConflicts, label: 'Merge conflicts', diagramLabel: 'Merge conflicts', kind: 'review', variant: 'chip', col: 2, row: 'review', slot: 1, failure: true,
    // A mechanical blocker the author has to resolve (rebase / merge main), so it carries the same
    // alarm tone as a failing test rather than the calm review blue. It sits on its own path because a
    // PR can be reviewing (or have changes requested) and be conflicted at the same time.
    description: 'The PR has merge conflicts with its base branch. GitHub blocks the merge until they are resolved — rebase or merge the base branch into it.' },
  { id: NODE.changesRequested, label: 'Changes requested', diagramLabel: 'Changes requested', kind: 'review', variant: 'box', col: 3, row: 'review', slot: 0,
    description: 'A reviewer requested changes. GitHub blocks the merge until the request is resolved; addressing it moves the PR back to Reviewing.' },
];

const TERMINAL_NODES: WorkflowNodeDef[] = [
  { id: NODE.ready, label: 'Ready', diagramLabel: 'Ready', kind: 'terminal', variant: 'gate', col: 4, row: 'review', slot: 0,
    description: 'Every required step is green or unnecessary: approved, every required test passed or was verified, nothing failing or running, no conflicts. Ready to merge.',
    details: ['Merge it on GitHub when you\'re ready.'] },
  // The release backlog rather than a trailing window of recent merges: this is what is waiting to
  // ship, so a PR stays here however long ago it merged and leaves the moment its work reaches the
  // release branch. Nothing here is anyone's to-do — it is the queue the Deployer works through.
  { id: NODE.merged, label: 'Merged', diagramLabel: 'Merged', kind: 'terminal', variant: 'box', col: 5, row: 'review', slot: 0,
    description: 'The PR has merged into origin/' + MAIN_BRANCH + ' and has not yet reached origin/' + RELEASE_BRANCH + '. This is the release backlog: what has landed but not shipped.',
    details: [
      'Next, the Deployer merges the changes on ' + MAIN_BRANCH + ' that aren\'t yet on the ' + RELEASE_BRANCH + ' branch, publishes them as a release, and rolls them out. A PR leaves this stage when that merge carries it across.',
      'There is no time limit here — a PR that merged weeks ago and still hasn\'t been released is exactly what this stage is for.',
    ],
    links: [{ label: 'Deployer (release.finvari.com)', href: DEPLOYER_URL }] },
];

const UNIT_NODES: WorkflowNodeDef[] = [
  // Count-only queue directly under Open, parallel to each scoped lane's "needs" box: the unit tests
  // haven't started yet. Separated from Unit testing so a PR GitHub has quietly declined to test does
  // not sit forever in a box that claims tests are running.
  { id: NODE.unitNeeded, label: 'Needs unit tests', diagramLabel: '', kind: 'unit', variant: 'count', col: 1, row: 'unit', slot: 0,
    description: 'GitHub has not registered any check runs for the latest commit of this PR, so its unit tests have not started.',
    details: [
      'Normally this is the moment right after a push, before GitHub queues the jobs, and the PR moves on to Unit testing within a minute.',
      'A PR that stays here is one GitHub is declining to test, which happens when our GitHub Actions minutes are exhausted for the billing period. (GitHub also skips the tests on a PR with merge conflicts, but such a PR is shown under Merge conflicts rather than here — resolving the conflicts starts the tests.)',
    ] },
  { id: NODE.unitTesting, label: 'Unit testing', diagramLabel: 'Unit testing', kind: 'unit', variant: 'box', col: 2, row: 'unit', slot: 0,
    description: 'GitHub is running the unit tests on the latest commit of this PR.',
    details: ['Unit tests run automatically for every PR — they are not scoped by changed files the way the Recompute and e2e lanes are.'] },
  // Terminal side state hanging below the unit line, so the unit lane reads top-down like the scoped
  // lanes: the "pass" line into Ready first, then failures beneath it.
  // The diagram label is just 'Failures' to match each scoped lane's failure chip; the lane it hangs
  // off names it. The full label keeps "Unit" for the panel heading and the table's stage chip.
  { id: NODE.unitFailures, label: 'Unit failures', diagramLabel: 'Failures', kind: 'unit', variant: 'chip', col: 3, row: 'unit', slot: 1, failure: true,
    description: 'GitHub reports failing unit-test checks on the latest commit.',
    details: ['Open the PR\'s Checks tab on GitHub to see which suites failed.'] },
];

function testLaneNodes(): WorkflowNodeDef[] {
  return TEST_LANES.flatMap(lane => {
    const ids = laneNodeIds(lane.id);
    // Links shared by every "run" node so a dev can go read the lane's test code (plus any extras).
    const runLinks: HelpLink[] = [
      { label: lane.label + ' source', href: githubTreeUrl(lane.sourcePath) },
      ...(lane.extraHelpLinks ?? []),
    ];
    // The bottom (slot-2) side state differs by lane kind. A pass/fail lane hangs a "Failures" alarm
    // chip there; a verify lane hangs a "Verify" chip in the same slot instead — a finished run waits
    // there for a human to inspect its results, so it is a to-do (no alarm tone) rather than a failure.
    const bottomNode: WorkflowNodeDef = lane.verify
      ? { id: ids.verify, label: 'Verify ' + lane.label, diagramLabel: 'Verify', kind: 'test', variant: 'chip', laneId: lane.id, col: 3, row: lane.id, slot: 2,
          description: 'The most recent ' + lane.label + ' has finished but no one has verified it yet. Open the run from the PR\'s ' + lane.label + ' tab and work through the Patterns tab: the system groups every diff, log warning, and attestation anomaly into review items, and you must accept each one (or reject it and rerun). Once every item is accepted, use "Mark verified".',
          details: [lane.summary, 'The changes are grouped so you review each distinct shape of change once rather than reading every patch. Mechanical noise (masked timestamps, environment URLs) is grouped and flagged with a hint to remove it at source, but still needs an explicit accept. Verification is blocked until every item is accepted, and the decision is recorded on the run for audit.'],
          links: runLinks }
      : { id: ids.failed, label: lane.label + ' failures', diagramLabel: 'Failures', kind: 'test', variant: 'chip', laneId: lane.id, col: 3, row: lane.id, slot: 2, failure: true,
          description: 'The most recent ' + lane.label + ' failed and has not been rerun. Open the run from the PR\'s ' + lane.label + ' tab to inspect what went wrong; if the differences are intended, rerun against a new baseline.',
          details: [lane.summary], links: runLinks };

    // Retest? explains what makes the prior run stale: for a verify lane it is the last *verified*
    // run that is behind head, for a pass/fail lane it is the last passing run.
    const retestDescription = lane.verify
      ? 'The last ' + lane.label + ' you verified is behind the PR head — new commits landed since. Either rerun it, or use "Accept without rerun" if the new commits can\'t affect the result.'
      : 'The last ' + lane.label + ' passed but new commits landed since. Either rerun it, or use "Accept without rerun" if the new commits can\'t affect the result.';

    return [
      // Count-only queue: placement directly under Open plus the panel's stage explanation carry the meaning, so it has no text and no incoming edge.
      { id: ids.needed, label: 'Needs ' + lane.label, diagramLabel: '', kind: 'test' as const, variant: 'count' as const, laneId: lane.id, col: 1, row: lane.id, slot: 0 as const,
        description: lane.label + ' is required because a changed file matches its path rules, but no run exists for the head commit yet. This is a queue — start a run from the PR\'s ' + lane.label + ' tab.',
        details: lane.neededDetails ?? [lane.summary],
        // The same array the derivation uses, so the help can never drift from the actual rule.
        pathRules: lane.requiredWhen,
        // Link to the lane's glossary stage (its "testing" node) when it has one, so a dev can read the
        // full vocabulary without hunting for it.
        ...(lane.testingDetails ? { stageLinks: [ids.running] } : {}) },
      // The running node's label uses shortLabel ('Recompute Testing') rather than lane.label + ' running'
      // so its panel heading and table stage chip read as the title-cased stage the diagram shows.
      { id: ids.running, label: lane.shortLabel, diagramLabel: lane.shortLabel, kind: 'test' as const, variant: 'box' as const, laneId: lane.id, col: 2, row: lane.id, slot: 0 as const,
        description: 'A ' + lane.label + ' is currently running on an environment. Follow it from the PR\'s ' + lane.label + ' tab.',
        details: lane.testingDetails ?? [lane.summary], links: runLinks },
      // Retest? sits directly under the line (tier 1) and Failures/Verify below it (tier 2), so
      // top-to-bottom each lane reads pass -> retest -> (failure|verify), keeping one lane's states
      // clear of the next lane's.
      { id: ids.retest, label: 'Retest ' + lane.label + '?', diagramLabel: 'Retest?', kind: 'test' as const, variant: 'chip' as const, laneId: lane.id, col: 3, row: lane.id, slot: 1 as const,
        description: retestDescription,
        details: [lane.summary], links: runLinks },
      bottomNode,
    ];
  });
}

export const WORKFLOW_NODES: WorkflowNodeDef[] = [
  PARKED_GROUP_NODE,
  ...PARKED_NODES,
  ...REVIEW_NODES,
  ...TERMINAL_NODES,
  ...UNIT_NODES,
  ...testLaneNodes(),
];

export const WORKFLOW_NODES_BY_ID: Record<string, WorkflowNodeDef> = Object.fromEntries(
  WORKFLOW_NODES.map(n => [n.id, n])
);

function testLaneEdges(): WorkflowEdgeDef[] {
  return TEST_LANES.flatMap(lane => {
    const ids = laneNodeIds(lane.id);
    if (lane.verify) {
      // A verify lane's finished run always lands in Verify first — it can't reach Ready unverified —
      // so the pass/stale split emanates from Verify, mirroring where a pass/fail lane splits from its
      // running box: verified at head flows to Ready, verified-then-behind-head drops to Retest?, and
      // a rerun loops back to testing. "testing" has the single forward exit into Verify.
      return [
        { source: ids.needed, target: ids.running },
        { source: ids.running, target: ids.verify },
        { source: ids.verify, target: NODE.ready },
        { source: ids.verify, target: ids.retest, sourceSide: 'top', targetSide: 'bottom' },
        { source: ids.retest, target: NODE.ready },
        { source: ids.retest, target: ids.running, sourceSide: 'left', targetSide: 'right' },
      ];
    }
    return [
      // No open -> needed edge: the needs box sits directly under Open, so its placement is the cue.
      { source: ids.needed, target: ids.running },
      { source: ids.running, target: ids.failed },
      { source: ids.running, target: NODE.ready },
      { source: ids.running, target: ids.retest },
      { source: ids.retest, target: NODE.ready },
      // Rerun loops back to testing; drawn on the same sides as running -> retest so the pair reads as one double-headed link.
      { source: ids.retest, target: ids.running, sourceSide: 'left', targetSide: 'right' },
    ];
  });
}

export const WORKFLOW_EDGES: WorkflowEdgeDef[] = [
  { source: PARKED_GROUP, target: NODE.open },
  { source: NODE.open, target: NODE.reviewing },
  // Reviewing and Changes requested form a double-headed pair on the flow line (a PR bounces between
  // them as review comments and fixes go back and forth), drawn on the same sides as the test lanes'
  // retest <-> running pair. An approval isn't a node: the PR leaves the row for Ready, so the only
  // forward exit from the review flow line into Ready is Changes-requested-resolved -> reviewing ->
  // (approved) Ready, plus the direct Changes requested -> Ready when a reviewer approves after
  // having requested changes.
  { source: NODE.reviewing, target: NODE.changesRequested },
  { source: NODE.changesRequested, target: NODE.reviewing, sourceSide: 'left', targetSide: 'right' },
  { source: NODE.changesRequested, target: NODE.ready },
  // Merge conflicts hangs below the review line on its own path: a PR can be conflicted while it is
  // still being reviewed, so it is not on the Open -> Reviewing chain. Resolving it (or approval) is
  // what carries it into Ready.
  { source: NODE.open, target: NODE.mergeConflicts, sourceSide: 'bottom', targetSide: 'left' },
  { source: NODE.mergeConflicts, target: NODE.ready },
  { source: NODE.ready, target: NODE.merged },
  // Like the scoped lanes, no Open -> needs-unit edge: the queue box sits directly under Open, so its
  // placement is the cue, and the unit line starts from the queue.
  { source: NODE.unitNeeded, target: NODE.unitTesting },
  { source: NODE.unitTesting, target: NODE.unitFailures },
  { source: NODE.unitTesting, target: NODE.ready },
  ...testLaneEdges(),
];
